import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';

/** Radix refuses an empty item value: this one stands for "nothing selected". */
const NONE = '__none__';

export type SimpleSelectOption = { value: string; label: string; disabled?: boolean };

export type SimpleSelectProps = {
  id?: string;
  /** `null` or `''`: nothing selected (placeholder, or the `noneLabel` row). */
  value: string | null;
  onChange: (value: string | null) => void;
  options: readonly SimpleSelectOption[];
  placeholder?: string;
  /** Adds a first row that clears the value ("— Aucune —"). */
  noneLabel?: string;
  disabled?: boolean;
  invalid?: boolean;
  'aria-label'?: string;
  'aria-describedby'?: string;
  className?: string;
  'data-testid'?: string;
};

/** A fixed list (Radix Select in the field look) — replaces native `<select>`. */
export function SimpleSelect({
  id,
  value,
  onChange,
  options,
  placeholder,
  noneLabel,
  disabled,
  invalid,
  'aria-label': ariaLabel,
  'aria-describedby': describedBy,
  className,
  'data-testid': testId,
}: SimpleSelectProps) {
  // Always controlled: '' (not undefined) shows the placeholder, so clearing the value from outside
  // resets the trigger instead of leaving the last choice on screen.
  const current = value === null || value === '' ? (noneLabel ? NONE : '') : value;
  return (
    <Select
      value={current}
      onValueChange={(next) => onChange(next === NONE ? null : next)}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        aria-label={ariaLabel}
        aria-describedby={describedBy}
        aria-invalid={invalid || undefined}
        data-testid={testId}
        className={cn('w-full', className)}
      >
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent>
        {noneLabel && <SelectItem value={NONE}>{noneLabel}</SelectItem>}
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value} disabled={option.disabled}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
